/* eslint-disable import/no-duplicates */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { Chart } from 'react-google-charts';

import { format, parseISO } from 'date-fns';

import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../../../services/api';

import { formatPrice, reducer, TIPO_USUARIO } from '../../../../utils/funcoes';

import { useToast } from '../../../../hooks/toast';
import { useAuth } from '../../../../hooks/auth';

import Input from '../../../../components/Input';
import Loading from '../../../../components/Loading';

import getValidationErrors from '../../../../utils/getValidationErrors';

import { RelatorioHeader } from './styles';
import CardCadastro from '../Cadastro/CardCadastro';
import getResponseErrors from '../../../../utils/getResponseErrors';
import HeaderSimples from '../../components/HeaderSimples';

interface IFormBusca {
  dataInicio: Date;
  dataFim: Date;
}

export interface IPaginacao {
  termoBusca: IFormBusca;
  limite: number;
  pagina: number;
}

interface IDataExibe {
  dataHora: string;
  dataDiaSemana: string;
}

interface IGenericType {
  [key: string]: any;
}

const ResumoCobrancas: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();

  useEffect(() => {
    if (user.UsuTipo >= TIPO_USUARIO.ATENDENTE_CAIXA) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  const { addToast } = useToast();

  const formRefBusca = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [graficoCobrancas, setGraficoCobrancas] = useState<IGenericType[]>();
  const [graficoRecebidos, setGraficoRecebidos] = useState<IGenericType[]>();
  const [tabelaCobrancas, setTabelaCobrancas] = useState<IGenericType[]>();
  const [tabelaRecebidos, setTabelaRecebidos] = useState<IGenericType[]>();

  const initialData = {
    dataInicio: format(new Date(), 'yyyy-MM-dd'),
    dataFim: format(new Date(), 'yyyy-MM-dd'),
  };
  const [dataExibe, setDataExibe] = useState<IDataExibe>();

  const buscaDados = useCallback(
    async (DataIni: Date, DataFim: Date) => {
      setLoading(true);
      try {
        const dataInicioSelecionada = parseISO(DataIni.toString());
        const dataFimSelecionada = parseISO(DataFim.toString());

        const dataInicioFormatada = format(dataInicioSelecionada, 'yyyy-MM-dd');
        const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

        setDataExibe({
          dataDiaSemana: `${format(
            dataInicioSelecionada,
            'dd/MM/yyyy',
          )} até ${format(dataFimSelecionada, 'dd/MM/yyyy')}`,
          dataHora: format(new Date(), 'dd/MM/yyyy HH:mm'),
        });

        const response = await api.get<IGenericType[]>(
          `/cobranca/realizadas?DataIni=${dataInicioFormatada}&DataFim=${dataFimFormatada}`,
        );

        const graficoCobradoLegenda: IGenericType[] = [
          ['-31 dias', '+31 dias'],
        ];

        const cobrancaGrafico: IGenericType[] = response.data[0].cobrancas;

        setGraficoCobrancas(graficoCobradoLegenda.concat(cobrancaGrafico));
        const totais = cobrancaGrafico.map(item => item[1]);
        const graficoCobradoTotais: IGenericType[] = [
          ['Total', totais.reduce(reducer, 0)],
        ];
        setTabelaCobrancas(cobrancaGrafico.concat(graficoCobradoTotais));
        const graficoRecebidoLegenda: IGenericType[] = [
          ['Recebido', 'Cobrado'],
        ];

        const recebidoGrafico: IGenericType[] = response.data[1].valores;
        setTabelaRecebidos(recebidoGrafico);
        setGraficoRecebidos(graficoRecebidoLegenda.concat(recebidoGrafico));
      } catch (error) {
        const mensagem = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Não foi possível carregar dados de cobrança',
          description: mensagem,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const handleSubmitBusca = useCallback(
    async (dados: IFormBusca) => {
      try {
        formRefBusca.current?.setErrors({});
        const schema = Yup.object().shape({
          dataInicio: Yup.date().required(),
          dataFim: Yup.date().required(),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });
        await buscaDados(dados.dataInicio, dados.dataFim);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefBusca.current?.setErrors(errors);
        }
      } finally {
        setLoading(false);
      }
    },
    [buscaDados],
  );

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}
      <>
        <div className="d-print-none bg-white border p-2 mb-2">
          <div className="p-2 mb-2">
            <h2 id="topoBusca">Resumo de Cobranças</h2>
          </div>

          <Form
            ref={formRefBusca}
            initialData={initialData}
            onSubmit={handleSubmitBusca}
          >
            <Row>
              <Col md={2}>
                <Input name="dataInicio" label="De" type="date" required />
              </Col>
              <Col md={2}>
                <Input name="dataFim" label="Até" type="date" required />
              </Col>

              <Col md={2}>
                <br />
                <Button type="submit" size="sm" variant="outline-secondary">
                  BUSCAR
                </Button>
              </Col>
            </Row>
          </Form>
        </div>

        <div className="bg-white border p-2 my-3">
          <HeaderSimples dataImpressao={dataExibe?.dataHora} />
        </div>

        <RelatorioHeader>
          RESUMO DE COBRANÇAS {dataExibe?.dataDiaSemana}
        </RelatorioHeader>
        <div className="bg-white border p-2">
          {graficoCobrancas &&
            graficoRecebidos &&
            tabelaRecebidos &&
            tabelaCobrancas && (
              <>
                <CardCadastro title="COBRANÇAS PAGAS / ESFORÇO COBRANÇAS">
                  <Row>
                    <Col xl="6" md="6">
                      <Chart
                        chartType="PieChart"
                        data={graficoCobrancas}
                        options={{
                          title: `Esforço de Cobrança`,
                          is3D: true,
                        }}
                        loader={<div>carregando...</div>}
                        width="100%"
                        height="300px"
                        legendToggle
                      />
                    </Col>
                    <Col xl="6" md="6">
                      <Chart
                        chartType="PieChart"
                        data={graficoRecebidos}
                        options={{
                          title: `Cobrado/Recebido`,
                          is3D: true,
                        }}
                        loader={<div>carregando...</div>}
                        width="100%"
                        height="300px"
                        legendToggle
                      />
                    </Col>
                  </Row>
                </CardCadastro>
                <br />
                <Row>
                  <Col xl="6" md="6">
                    <CardCadastro title="Resumo de Cobranças">
                      <table
                        className="table table-hover table-sm table-font-small table-bordered"
                        style={{ borderWidth: '0px' }}
                      >
                        <thead>
                          <tr className="text-center">
                            <th>Cobranças</th>
                            <th style={{ width: '150px' }}>Qtd</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tabelaCobrancas.map(item => (
                            <tr
                              key={`cobranca-${item[0]}`}
                              style={{ fontSize: '.7rem', height: '30px' }}
                            >
                              <td>{item[0]}</td>
                              <td>{item[1]}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </CardCadastro>
                  </Col>
                  <Col xl="6" md="6">
                    <CardCadastro title="Resumo de valores Recebidos">
                      <table
                        className="table table-hover table-sm table-font-small table-bordered"
                        style={{ borderWidth: '0px' }}
                      >
                        <thead>
                          <tr className="text-center">
                            <th>Cobranças</th>
                            <th style={{ width: '150px' }}>Valor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tabelaRecebidos.map(item => (
                            <tr
                              key={`recebidos-${item[0]}`}
                              style={{ fontSize: '.7rem', height: '30px' }}
                            >
                              <td>{item[0]}</td>
                              <td>{formatPrice(item[1])}</td>
                            </tr>
                          ))}
                          <tr style={{ fontSize: '.7rem', height: '30px' }}>
                            <td colSpan={2}>
                              * Acobrança pode ter ocorrido em outro período
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </CardCadastro>
                  </Col>
                </Row>
              </>
            )}
          {!graficoRecebidos && (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: '250px' }}
            >
              <h5>Nenhum registro encontrado</h5>
            </div>
          )}
        </div>
      </>
    </Container>
  );
};

export default ResumoCobrancas;
